import type { JSX } from "react";
import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useSceneStore } from "@/state/useSceneStore";
import { MonitorFrame } from "@/ui/MonitorContent/MonitorFrame";
import { ProjectsIndex } from "@/ui/MonitorContent/Projects/Index";
import { CaseStudy } from "@/ui/MonitorContent/Projects/CaseStudy";
import { ContactForm } from "@/ui/MonitorContent/Contact/Form";
import { ErrorBoundary } from "@/ui/ErrorBoundary";
import { Meta } from "@/ui/Meta";
import { PROJECTS } from "@/ui/MonitorContent/Projects/data/projects";

const DEFAULT_ROUTE = "#/projects";
const SITE = "Adam Kotlorz";

const handleClose = (): void => {
  useSceneStore.getState().setActiveSection(null);
  useSceneStore.getState().setActiveMonitorRoute(null);
  if (typeof window !== "undefined") {
    history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search,
    );
  }
};

export const MonitorRouter = (): JSX.Element => {
  const route = useSceneStore((s) => s.activeMonitorRoute) ?? DEFAULT_ROUTE;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (route === "#/about" || route === "#/now") {
      useSceneStore.getState().setActiveMonitorRoute("#/projects");
      return;
    }
    const desired = route.replace(/^#/, "");
    if (window.location.hash !== desired) {
      history.replaceState(null, "", `${window.location.pathname}#${desired}`);
    }
  }, [route]);

  let body: JSX.Element;
  let meta: JSX.Element;

  if (route.startsWith("#/projects/")) {
    const slug = route.replace("#/projects/", "");
    const project = PROJECTS.find((p) => p.slug === slug);
    meta = (
      <Meta
        title={project ? `${project.title} - Adam Kotlorz` : SITE}
        description={project?.summary ?? "Adam Kotlorz"}
        canonical={`https://adamkotlorz.dev/${route.replace(/^#/, "#")}`}
      />
    );
    body = <CaseStudy slug={slug} />;
  } else if (route === "#/contact") {
    meta = (
      <Meta
        title={`Contact - ${SITE}`}
        description="Reach Adam Kotlorz for work or collaboration."
      />
    );
    body = <ContactForm />;
  } else {
    meta = (
      <Meta
        title={`Projects - ${SITE}`}
        description="Selected work in three.js, R3F, React, ML."
      />
    );
    body = <ProjectsIndex />;
  }

  return (
    <MonitorFrame route={route} onClose={handleClose}>
      {meta}
      <ErrorBoundary
        fallback={
          <p className="font-mono text-xs text-danger">
            signal lost ·{" "}
            <button
              type="button"
              onClick={() => location.reload()}
              className="underline"
            >
              retry
            </button>
          </p>
        }
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={route}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            {body}
          </motion.div>
        </AnimatePresence>
      </ErrorBoundary>
    </MonitorFrame>
  );
};
